import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronLeft, Library, Loader2, MessageSquare, Plus, Save, Sparkles } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import EmptyState from 'components/ui/EmptyState';
import { SegmentedTabs } from 'components/ui/data-table';
import { useToast } from 'components/ui/use-toast';
import { HeaderIcon, ScreenHeader, Segmented } from 'components/mobile';
import { Chat, QuizArtifactEditor, CreateAssessmentSubjectModal } from 'components/learn';
import { GenerateToBankModal } from 'components/questionBank/GenerateToBankModal';
import { GeneratedQuestionsDialog } from 'components/questionBank/GeneratedQuestionsDialog';
import { useArtifactBankedStatus } from 'modules/questionBank';
import * as Document from 'modules/document';
import * as LearnArtifacts from 'modules/learnArtifacts';
import type { LearnArtifact } from 'modules/learnArtifacts';
import { useAuthContext } from 'modules/auth';
import { PERMISSIONS } from 'modules/auth/permissions';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import { SourcesPanel } from './SourcesPanel';
import { StudioPanel } from './StudioPanel';
import { CollapsedRail, WorkspacePanel } from './WorkspacePanel';
import { QuizGenerateDialog, QuizGenConfig } from './QuizGenerateDialog';
import {
  MAX_CHAT_SOURCES,
  SourceDoc,
  defaultSelection,
  pickChatSources,
  toggleAllSelection,
  withSource,
} from './workspace.types';

/** Which panel shows where the three columns can't sit side by side (phones, tablets). */
type MobileTab = 'sources' | 'workspace' | 'studio';

const ACCEPT = '.pdf,.docx,.doc,application/pdf';

/** The Notebooks grid; the workspace's way back. */
const NOTEBOOKS_PATH = '/app/documents';

interface NotebookWorkspaceProps {
  /** The notebook this workspace is scoped to: its sources, its one chat and its generated items. */
  notebookId: string;
  /** Already localised (see `getNotebookTitle`); shown in the header next to the way back. */
  title: string;
  /** Phones (<768px): the screen's own header with the add-source icon and a panel switcher. */
  phone?: boolean;
}

/** One notebook. Chat spans every CHECKED source (RAG); the Studio acts on the single ACTIVE one and lists the whole notebook's outputs. */
export const NotebookWorkspace: React.FC<NotebookWorkspaceProps> = ({ notebookId, title, phone = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { state: authState } = useAuthContext();

  const canManage = authState.permissions.includes(PERMISSIONS.DOCUMENTS_EDIT);
  const canChat = authState.permissions.includes(PERMISSIONS.AI_LEARNING_VIEW);
  const canCreateBank = authState.permissions.includes(PERMISSIONS.QUESTIONS_CREATE);
  // Document → question-bank generation is a paid capability (Growth+). Keep the
  // Studio tool visible (premium-affordance pattern) but open the upgrade dialog
  // on click when the plan doesn't include it, instead of a doomed 403 request.
  const canBulkGeneration = useFeature(FEATURES.BULK_GENERATION);
  const openUpgrade = useOpenUpgradeForFeature();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openArtifactId, setOpenArtifactId] = useState<string | null>(null);
  // Bank drafts review in their own full-bleed dialog rather than the centre
  // column, which stays the quiz-draft surface.
  const [bankDraftArtifactId, setBankDraftArtifactId] = useState<string | null>(null);
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [bankPickerOpen, setBankPickerOpen] = useState(false);
  const [sourcesCollapsed, setSourcesCollapsed] = useState(false);
  const [studioCollapsed, setStudioCollapsed] = useState(false);
  const [mobileTab, setMobileTab] = useState<MobileTab>('workspace');
  const [quizDraft, setQuizDraft] = useState<LearnArtifacts.QuizSpec | null>(null);
  const [quizBaseline, setQuizBaseline] = useState<string | null>(null);
  const selectionInit = useRef(false);
  // One file picker for the Sources panel's "Manba qo'shish" and the phone header's plus.
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Everything this workspace caches is keyed by the notebook.
  const documentsKey = ['documents', notebookId];
  const artifactsKey = ['learn-artifacts', 'notebook', notebookId];

  const documentsQuery = useQuery({
    queryKey: documentsKey,
    queryFn: async () => {
      const { data } = await Document.Api.GetAll(notebookId);
      return data as { success: boolean; data: SourceDoc[] };
    },
    refetchInterval: (query) => {
      const docs = query.state.data?.data ?? [];
      return docs.some((d) => d.processingStatus === 'pending' || d.processingStatus === 'processing') ? 3000 : false;
    },
    refetchIntervalInBackground: false,
  });
  const documents = useMemo(() => documentsQuery.data?.data ?? [], [documentsQuery.data]);

  // First load: tick the sources (at most MAX_CHAT_SOURCES, in list order) and make the first one active.
  useEffect(() => {
    if (!selectionInit.current && documents.length) {
      selectionInit.current = true;
      setSelectedIds(defaultSelection(documents));
      setActiveId((cur) => cur ?? documents[0]._id);
    }
  }, [documents]);

  const activeDoc = useMemo(() => documents.find((d) => d._id === activeId) ?? null, [documents, activeId]);
  const activeReady = activeDoc?.processingStatus === 'completed';

  // Chat context = the selected sources that are ready (indexed). RAG retrieval
  // spans all of them, so the user chats across every checked document at once;
  // a single selected source behaves exactly as before. Sources still mid-
  // processing are excluded until their chunks exist.
  const chatDocs = useMemo(() => pickChatSources(documents, selectedIds), [documents, selectedIds]);
  const chatDocIds = useMemo(() => chatDocs.map((d) => d._id), [chatDocs]);
  // Combined source label sent to the model for grounding. Capped to the DTO's
  // documentTitle limit (500) so long/many titles never 400 the request.
  const chatLabel = useMemo(() => chatDocs.map((d) => d.title).join(', ').slice(0, 500), [chatDocs]);

  // The Studio lists the whole notebook's outputs, whichever source is active
  // (generation below still runs against the active source).
  const artifactsQuery = useQuery({
    queryKey: artifactsKey,
    queryFn: () => LearnArtifacts.Api.listArtifacts({ notebookId }),
    refetchInterval: (query) => ((query.state.data ?? []).some((a) => a.status === 'generating') ? 3000 : false),
    refetchIntervalInBackground: false,
  });
  const artifacts = useMemo(() => artifactsQuery.data ?? [], [artifactsQuery.data]);
  const openArtifact = useMemo(
    () => artifacts.find((a) => a.id === openArtifactId) ?? null,
    [artifacts, openArtifactId],
  );

  // Document → bank generation runs as a background job, so the dialog that
  // started it is long gone by the time the questions land. Watch the polled
  // artifacts for bank jobs that just settled and report readiness. Each
  // artifact is announced once.
  //
  // Nothing is written to the bank here any more: a finished job produces a
  // reviewable DRAFT, so there are no bank/subject caches to invalidate (that
  // moved to the review dialog's commit) and the toast reports questions ready
  // to review rather than added. It deliberately does NOT open the dialog — the
  // job survives navigation and these runs take minutes, so a full-bleed dialog
  // appearing over unrelated work would be hostile.
  const announcedBankJobs = useRef(new Set<string>());
  const previousStatuses = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    for (const artifact of artifacts) {
      if (artifact.type !== 'bank_batch') continue;
      const prevStatus = previousStatuses.current.get(artifact.id);
      previousStatuses.current.set(artifact.id, artifact.status);

      if (prevStatus !== 'generating') continue;
      if (artifact.status === 'generating') continue;
      if (announcedBankJobs.current.has(artifact.id)) continue;
      announcedBankJobs.current.add(artifact.id);

      if (artifact.status === 'failed') {
        toast.error(t('generation_failed'), {
          description: artifact.errorMessage || t('please_try_again'),
        });
        continue;
      }

      const bank = (artifact.metadata?.bank ?? {}) as {
        subjectName?: string;
        questionsGenerated?: number;
      };
      toast.success(t('questions_generated'), {
        description: t('n_questions_ready_to_review', { count: bank.questionsGenerated ?? 0 }),
        action: {
          label: t('pipeline_review'),
          onClick: () => setBankDraftArtifactId(artifact.id),
        },
      });
    }
  }, [artifacts, t, toast]);

  // Opening a review (from the Studio panel or a fresh generation) surfaces it in
  // the center column — on mobile that column is only shown on the workspace tab,
  // so pull the user there. Inert on desktop, where all columns are visible.
  useEffect(() => {
    if (openArtifactId) setMobileTab('workspace');
  }, [openArtifactId]);

  // Load the open artifact's quiz payload into the editable draft.
  useEffect(() => {
    if (!openArtifact) {
      setQuizDraft(null);
      setQuizBaseline(null);
      return;
    }
    const parsed = LearnArtifacts.parseQuizPayload(openArtifact.payload) || null;
    setQuizDraft(parsed);
    setQuizBaseline(parsed ? JSON.stringify(parsed) : null);
  }, [openArtifact]);

  const isQuizDirty = useMemo(() => {
    if (!quizDraft || quizBaseline === null) return false;
    return JSON.stringify(quizDraft) !== quizBaseline;
  }, [quizDraft, quizBaseline]);
  const quizValidation = useMemo(
    () => (quizDraft ? LearnArtifacts.quizSpecSchema.safeParse(quizDraft) : null),
    [quizDraft],
  );

  const { data: bankedStatus } = useArtifactBankedStatus(
    canCreateBank && openArtifact?.type === 'quiz' && openArtifact?.status === 'ready' ? openArtifact.id : undefined,
  );

  const bankDraftArtifact = useMemo(
    () => artifacts.find((a) => a.id === bankDraftArtifactId) ?? null,
    [artifacts, bankDraftArtifactId],
  );
  // A committed draft is read-only — the bank dedupes per artifact, so the
  // questions left out of that commit can never be added.
  const { data: bankDraftBankedStatus } = useArtifactBankedStatus(
    bankDraftArtifact?.id ?? undefined,
  );

  // ---- Mutations -----------------------------------------------------------
  const uploadMutation = useMutation({
    mutationFn: async (file: File) =>
      Document.Api.Upload(file, file.name.replace(/\.(pdf|docx|doc)$/i, ''), { notebookId }),
    onSuccess: (result: any) => {
      const newId = result?.data?.data?._id || result?.data?.data?.id;
      queryClient.invalidateQueries({ queryKey: documentsKey });
      // The grid's card shows the notebook's source count.
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
      toast.success(t('document_uploaded'), { description: t('document_was_uploaded_successfully') });
      if (newId) {
        // Joins the chat's scope unless that is already at its limit (the Sources panel says so).
        setSelectedIds((prev) => withSource(prev, newId));
        setActiveId(newId);
      }
    },
    onError: (error: unknown) => {
      // Prefer the server's own explanation over axios' generic
      // "Request failed with status code 500".
      const serverMessage = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      toast.error(t('upload_failed'), {
        description: typeof serverMessage === 'string' ? serverMessage : (error as Error)?.message,
      });
    },
  });

  const generateQuizMutation = useMutation({
    mutationFn: async (config: QuizGenConfig) =>
      LearnArtifacts.Api.generateQuizArtifact({
        documentId: activeId as string,
        questionCount: config.questionCount,
        difficulty: config.difficulty,
        language: config.language,
        fieldKey: config.fieldKey || undefined,
        fieldPrompt: config.fieldPrompt.trim() || undefined,
      }),
    onSuccess: (artifact) => {
      setQuizDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: artifactsKey });
      // No auto-open: the Studio card tracks progress and offers Review when the
      // draft lands, same as a bank generation.
      toast.info(t('generating'), { description: t('we_are_preparing_your_assessment_draft') });
    },
    onError: (error: unknown) =>
      toast.error(t('assessment_draft_generation_failed'), {
        description: (error as Error)?.message || t('unable_to_generate_assessment_draft_now'),
      }),
  });

  const saveArtifactMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (!openArtifact) throw new Error('No generated item selected');
      return LearnArtifacts.Api.updateArtifactPayload(openArtifact.id, payload);
    },
    onSuccess: (_result, payload) => {
      setQuizBaseline(JSON.stringify(payload));
      queryClient.invalidateQueries({ queryKey: artifactsKey });
      toast.success(t('saved'), { description: t('your_changes_have_been_saved') });
    },
    onError: (error: unknown) =>
      toast.error(t('save_failed'), { description: (error as Error)?.message || t('failed_to_save_draft_changes') }),
  });

  const deleteArtifactMutation = useMutation({
    mutationFn: async (id: string) => LearnArtifacts.Api.deleteArtifact(id),
    onSuccess: (_result, deletedId) => {
      queryClient.invalidateQueries({ queryKey: artifactsKey });
      if (deletedId === openArtifactId) setOpenArtifactId(null);
    },
    onError: (error: unknown) =>
      toast.error(t('delete_failed'), { description: (error as Error)?.message || t('could_not_delete_generated_item') }),
  });

  // "Save to bank": persist the draft's questions into the chosen subject's
  // bank, then open that subject's Question Bank so the user can review and
  // configure them.
  const saveToBankMutation = useMutation({
    mutationFn: async (subjectId: string) => {
      if (!openArtifact) throw new Error('No quiz draft selected');
      // Persist on-screen edits first so the bank copy matches what's shown.
      if (isQuizDirty && quizDraft && quizValidation?.success) {
        await LearnArtifacts.Api.updateArtifactPayload(openArtifact.id, quizDraft as unknown as Record<string, unknown>);
        setQuizBaseline(JSON.stringify(quizDraft));
      }
      return LearnArtifacts.Api.saveArtifactToBank(openArtifact.id, { subjectId });
    },
    onSuccess: (result, subjectId) => {
      setBankPickerOpen(false);
      queryClient.invalidateQueries({ queryKey: ['question-bank'] });
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
      if (result.questionsAdded > 0) {
        toast.success(t('saved_to_bank'), { description: t('created_questions_count', { count: result.questionsAdded }) });
      } else {
        toast.info(t('already_in_bank'));
      }
      navigate(`/app/questions?subjectId=${subjectId}`);
    },
    onError: (error: unknown) =>
      toast.error(t('save_failed'), { description: (error as Error)?.message || t('failed_to_save_draft_changes') }),
  });

  // ---- Handlers ------------------------------------------------------------
  const toggleSelected = (id: string) => {
    // The chat takes at most MAX_CHAT_SOURCES checked sources: say so rather than ignore the click
    // (the box stays unticked because the set doesn't change).
    if (!selectedIds.has(id) && selectedIds.size >= MAX_CHAT_SOURCES) {
      toast.info(t('aigen.max_sources_title'), { description: t('aigen.max_sources', { count: MAX_CHAT_SOURCES }) });
      return;
    }
    setSelectedIds((prev) => {
      if (prev.has(id)) {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }
      return withSource(prev, id);
    });
  };
  // "All" is every source the chat can use (the processed ones, which is what the
  // Select-all box reflects), up to the limit; picking all also queues the ones still processing.
  const toggleAll = () => setSelectedIds((prev) => toggleAllSelection(documents, prev));

  const openFilePicker = () => {
    if (uploadMutation.isPending) return;
    fileInputRef.current?.click();
  };
  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveCurrentArtifact = () => {
    if (!openArtifact || !quizDraft || !quizValidation?.success) {
      toast.error(t('fix_quiz_validation_issues'), { description: t('quiz_payload_is_invalid_please_review_your_edits') });
      return;
    }
    saveArtifactMutation.mutate(quizDraft as unknown as Record<string, unknown>);
  };

  // ---- Center panel --------------------------------------------------------
  const renderReview = () => {
    if (!openArtifact) return null;
    if (openArtifact.status === 'generating') {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">{t('generating')}</h2>
          <p className="max-w-md text-sm text-muted-foreground">{t('we_are_preparing_your_assessment_draft')}</p>
        </div>
      );
    }
    if (openArtifact.status === 'failed') {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
          <Sparkles className="h-8 w-8 text-error-500" />
          <h2 className="text-lg font-semibold text-foreground">{t('generation_failed')}</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {openArtifact.errorMessage || t('try_generating_this_item_again_from_the_right_panel')}
          </p>
        </div>
      );
    }
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <button
            type="button"
            onClick={() => setOpenArtifactId(null)}
            aria-label={t('back')}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{openArtifact.title}</h2>
          {openArtifact.type === 'quiz' &&
            canCreateBank &&
            (bankedStatus?.banked ? (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-success-200 bg-success-50 px-2.5 py-1 text-xs font-medium text-success-700 dark:border-success-900 dark:bg-success-900/30 dark:text-success-300">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t('saved_to_bank')}
              </span>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setBankPickerOpen(true)} disabled={saveToBankMutation.isPending || !quizDraft}>
                {saveToBankMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Library className="mr-2 h-4 w-4" />}
                {t('save_to_bank')}
              </Button>
            ))}
          <Button size="sm" onClick={handleSaveCurrentArtifact} disabled={saveArtifactMutation.isPending || !isQuizDirty || !quizValidation?.success}>
            {saveArtifactMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {t('save_changes')}
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 scrollbar-thin">
          {openArtifact.type === 'quiz' && quizDraft && <QuizArtifactEditor value={quizDraft} onChange={setQuizDraft} />}
        </div>
      </div>
    );
  };

  const renderChat = () => {
    // Nothing to say until the sources are known (the Sources panel shows the skeleton or the retry).
    if (documentsQuery.isLoading || documentsQuery.isError) return null;
    // A new notebook has nothing to chat with yet: ask for a first source rather than "select one".
    if (documents.length === 0) {
      return (
        <EmptyState
          className="flex-1"
          icon={<MessageSquare />}
          title={t('aigen.no_sources')}
          action={canManage ? { label: t('aigen.add_sources'), icon: <Plus className="h-4 w-4" />, onClick: openFilePicker } : undefined}
        />
      );
    }
    if (chatDocs.length === 0) {
      return <EmptyState className="flex-1" icon={<Sparkles />} title={t('aigen.select_source')} />;
    }
    if (!canChat) {
      return (
        <EmptyState
          className="flex-1"
          icon={<Sparkles />}
          title={t('ai_chat_is_disabled')}
          description={t('ask_an_administrator_to_grant_the_learning_ai_chat_permission')}
        />
      );
    }
    // The notebook has ONE chat; the checked sources only narrow what it retrieves from.
    return (
      <div className="min-h-0 flex-1 overflow-hidden">
        <Chat
          notebookId={notebookId}
          documentIds={chatDocIds}
          documentTitle={chatLabel}
          isReady
          processingLabel={t('processing')}
        />
      </div>
    );
  };

  const workspaceLabel = openArtifact ? t('pipeline_review') : t('chat');
  // The chat head reads the single title, or "N ta manba" when chatting across several.
  const chatMeta =
    openArtifact || chatDocs.length === 0 ? undefined : (
      <span className="block max-w-[260px] truncate">
        {chatDocs.length === 1 ? chatDocs[0].title : t('aigen.n_sources', { count: chatDocs.length })}
      </span>
    );
  const panelTabs: { value: MobileTab; label: string; count?: number }[] = [
    { value: 'sources', label: t('aigen.sources'), count: documents.length || undefined },
    { value: 'workspace', label: workspaceLabel },
    { value: 'studio', label: t('aigen.studio'), count: artifacts.length || undefined },
  ];

  const sourcesPanel = (
    <SourcesPanel
      documents={documents}
      isLoading={documentsQuery.isLoading}
      isError={documentsQuery.isError}
      onRetry={() => documentsQuery.refetch()}
      activeId={activeId}
      onSelectActive={setActiveId}
      selectedIds={selectedIds}
      onToggleSelected={toggleSelected}
      onToggleAll={toggleAll}
      canManage={canManage}
      onAddSource={openFilePicker}
      uploading={uploadMutation.isPending}
      onCollapse={() => setSourcesCollapsed(true)}
      bare={phone}
    />
  );
  const centerPanel = (
    <WorkspacePanel title={workspaceLabel} meta={chatMeta} bare={phone}>
      {openArtifact ? renderReview() : renderChat()}
    </WorkspacePanel>
  );
  const studioPanel = (
    <StudioPanel
      artifacts={artifacts}
      isLoading={documentsQuery.isLoading || artifactsQuery.isLoading}
      isError={artifactsQuery.isError}
      onRetry={() => artifactsQuery.refetch()}
      hasActiveSource={Boolean(activeDoc)}
      activeTitle={activeDoc?.title}
      activeReady={Boolean(activeReady)}
      canGenerateBank={canCreateBank}
      bankPremium={!canBulkGeneration}
      onGenerateQuiz={() => setQuizDialogOpen(true)}
      onGenerateBank={() =>
        canBulkGeneration ? setBankModalOpen(true) : openUpgrade(FEATURES.BULK_GENERATION)
      }
      // One Review button, one behaviour. Both draft kinds open the
      // full-page dialog; the dialog adapts to the payload it's given.
      // Two different results from an identical-looking button was the
      // whole source of confusion here.
      onReview={(a) => setBankDraftArtifactId(a.id)}
      onDelete={(a: LearnArtifact) => deleteArtifactMutation.mutate(a.id)}
      deletingId={deleteArtifactMutation.isPending ? (deleteArtifactMutation.variables as string) : null}
      onCollapse={() => setStudioCollapsed(true)}
      bare={phone}
    />
  );

  const modals = (
    <>
      <input ref={fileInputRef} type="file" accept={ACCEPT} onChange={handlePickFile} className="hidden" tabIndex={-1} aria-hidden />
      <QuizGenerateDialog
        open={quizDialogOpen}
        onClose={() => setQuizDialogOpen(false)}
        onGenerate={(config) => generateQuizMutation.mutate(config)}
        isGenerating={generateQuizMutation.isPending}
      />
      {activeId && (
        <GenerateToBankModal documentId={activeId} open={bankModalOpen} onClose={() => setBankModalOpen(false)} />
      )}
      <GeneratedQuestionsDialog
        open={Boolean(bankDraftArtifact)}
        onClose={() => setBankDraftArtifactId(null)}
        artifact={bankDraftArtifact}
        banked={Boolean(bankDraftBankedStatus?.banked)}
      />
      <CreateAssessmentSubjectModal
        open={bankPickerOpen}
        onClose={() => setBankPickerOpen(false)}
        onConfirm={(subjectId) => saveToBankMutation.mutate(subjectId)}
        isSubmitting={saveToBankMutation.isPending}
        title={t('choose_a_subject', 'Choose a subject')}
        description={t(
          'add_generated_questions_to_subject_desc',
          "The generated questions will be added to this subject's question bank. Pick an existing subject or create a new one.",
        )}
        confirmLabel={t('save_to_bank', 'Save to bank')}
        confirmingLabel={t('adding', 'Adding…')}
      />
    </>
  );

  if (phone) {
    // DocumentsPage pins this inside a <MobileScreen> (`absolute inset-0` flex
    // column), so the header and the panel area divide a true screen height.
    return (
      <>
        <ScreenHeader
          title={title}
          onBack={() => navigate(NOTEBOOKS_PATH)}
          right={
            canManage ? (
              <HeaderIcon
                icon={uploadMutation.isPending ? <Loader2 size={19} className="animate-spin" /> : <Plus size={19} />}
                label={t('aigen.add_sources')}
                onClick={openFilePicker}
              />
            ) : undefined
          }
        >
          <div className="pb-3 pt-2.5">
            <Segmented
              full
              tabs={panelTabs}
              value={mobileTab}
              onChange={(value) => setMobileTab(value as MobileTab)}
              aria-label={t('nav.ai_generate')}
            />
          </div>
        </ScreenHeader>
        {/* One panel at a time on white. All three stay mounted, so an answer that
            is streaming keeps going while another tab is open. The bottom padding
            clears the docked 64px nav, whose top edge sits right under the composer. */}
        <div className="flex min-h-0 flex-1 flex-col bg-card pb-[calc(64px+env(safe-area-inset-bottom))]">
          {(
            [
              ['sources', sourcesPanel],
              ['workspace', centerPanel],
              ['studio', studioPanel],
            ] as const
          ).map(([tab, panel]) => (
            <div key={tab} data-panel={tab} className={cn('min-h-0 flex-1 flex-col', mobileTab === tab ? 'flex' : 'hidden')}>
              {panel}
            </div>
          ))}
        </div>
        {modals}
      </>
    );
  }

  return (
    // Height budget must match the shell's actual chrome per breakpoint
    // (layouts/Main/Main.tsx), not a single desktop-only constant (phones take
    // the branch above):
    // There is deliberately NO `min-h` floor at any breakpoint: every panel
    // scrolls internally, so a short window should shrink the workspace rather
    // than overflow the container — a floor taller than the available height is
    // just the composer-below-the-fold bug in another form.
    //  - `md`–`lg` (768–1023px): page card (`md:m-3` = 1.5rem vertical, plus
    //    its 1px top+bottom border) + header (3.5rem) + `main`'s `pt-2 pb-6`
    //    (2rem) = 7rem + 2px.
    //  - `lg`+ (1024px+): `main` switches to `pt-2 lg:pb-8` (2.5rem), so the
    //    card + border + header + padding consume 7.5rem + 2px.
    // Plus `--banner-h` at both: the subscription banner is a flex sibling
    // ABOVE the whole content row, so it shrinks `main` without touching
    // `100dvh`. Reading the variable the shell already publishes keeps this
    // exact whether or not the banner is up — hardcoding the constants made the
    // workspace 30-38px taller than its container and pushed the chat composer
    // below the card's visible area.
    // `dvh` (not `vh`) keeps this correct as mobile browser chrome resizes.
    <div className="flex h-full min-h-0 flex-col gap-3 md:h-[calc(100dvh-7rem-2px-var(--banner-h,0px))] lg:h-[calc(100dvh-7.5rem-2px-var(--banner-h,0px))]">
      {/* Way back to the grid, then the notebook's name. */}
      <header className="flex min-w-0 shrink-0 items-center gap-1.5">
        <Link
          to={NOTEBOOKS_PATH}
          className="-ml-1.5 inline-flex h-8 shrink-0 items-center gap-0.5 rounded-md pl-1 pr-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          {t('notebooks.title')}
        </Link>
        <span aria-hidden className="text-muted-foreground/40">
          /
        </span>
        <h1 className="min-w-0 truncate text-[15px] font-semibold text-foreground">{title}</h1>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        {/* Panel switcher — tablets only (`md`–`lg`), where the columns can't sit side by side */}
        <SegmentedTabs<MobileTab>
          className="shrink-0 self-start lg:hidden"
          options={panelTabs}
          value={mobileTab}
          onChange={setMobileTab}
          aria-label={t('nav.ai_generate')}
        />

        {/* Sources */}
        {sourcesCollapsed && (
          <CollapsedRail side="left" label={t('aigen.sources')} onExpand={() => setSourcesCollapsed(false)} />
        )}
        <div
          className={cn(
            'min-h-0 flex-1 lg:w-[300px] lg:flex-none lg:shrink-0',
            mobileTab === 'sources' ? 'block' : 'hidden',
            sourcesCollapsed ? 'lg:hidden' : 'lg:block',
          )}
        >
          {sourcesPanel}
        </div>

        {/* Chat / review */}
        <div className={cn('min-h-0 min-w-0 flex-1 lg:block', mobileTab === 'workspace' ? 'block' : 'hidden')}>
          {centerPanel}
        </div>

        {/* Studio */}
        {studioCollapsed && (
          <CollapsedRail side="right" label={t('aigen.studio')} onExpand={() => setStudioCollapsed(false)} />
        )}
        <div
          className={cn(
            'min-h-0 flex-1 lg:w-[320px] lg:flex-none lg:shrink-0',
            mobileTab === 'studio' ? 'block' : 'hidden',
            studioCollapsed ? 'lg:hidden' : 'lg:block',
          )}
        >
          {studioPanel}
        </div>
      </div>

      {modals}
    </div>
  );
};

export default NotebookWorkspace;
